import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";

export function Card({
  title,
  actions,
  children,
  flush,
  id,
  collapsed,
  onToggle,
  summary,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  /** Anchor for in-page links, and the base of the body's id. */
  id?: string;
  /**
   * With `onToggle` the card folds: the title becomes a button and, folded,
   * the body is hidden (kept mounted, so nothing inside loses its state).
   */
  collapsed?: boolean;
  onToggle?: () => void;
  /** One line said in the header while folded — what the hidden body holds. */
  summary?: ReactNode;
}) {
  const autoId = useId();
  const bodyId = `${id ?? autoId}-body`;
  const foldable = !!onToggle;
  const folded = foldable && !!collapsed;
  return (
    <section className={`card${foldable ? " card--foldable" : ""}${folded ? " is-folded" : ""}`} id={id}>
      {(title || actions) && (
        <header className="card__header">
          {foldable ? (
            <button
              type="button"
              className="card__toggle"
              aria-expanded={!folded}
              aria-controls={bodyId}
              onClick={onToggle}
            >
              <span className="card__chevron" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M3 4.5 6 7.5 9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="card__title">{title}</span>
              {folded && summary && <span className="card__summary">{summary}</span>}
            </button>
          ) : (
            <span className="card__title">{title}</span>
          )}
          {actions && !folded && <div className="row">{actions}</div>}
        </header>
      )}
      <div
        id={bodyId}
        className={flush ? "card__body card__body--flush" : "card__body"}
        hidden={folded || undefined}
      >
        {children}
      </div>
    </section>
  );
}

/** What a page hands a card to make it foldable; spread onto `Card`. */
export interface Fold {
  id?: string;
  collapsed: boolean;
  onToggle: () => void;
  summary?: ReactNode;
}

/**
 * Which foldable sections of a page are folded, remembered per browser.
 *
 * `defaults` says which start folded the first time; after that the
 * reader's own choice wins — somebody who folded the JSON card does not want
 * it back open on every visit.
 */
export function useFoldedSections(storageKey: string, defaults: Record<string, boolean> = {}) {
  const [folded, setFolded] = useState<Record<string, boolean>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) ?? "null") as Record<string, boolean> | null;
      return { ...defaults, ...(saved ?? {}) };
    } catch {
      return { ...defaults };
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(folded));
    } catch {
      // Private mode or storage off: folding still works for this visit.
    }
  }, [storageKey, folded]);
  return {
    isFolded: (id: string) => folded[id] ?? false,
    toggle: (id: string) => setFolded((f) => ({ ...f, [id]: !(f[id] ?? false) })),
    open: (id: string) => setFolded((f) => (f[id] ? { ...f, [id]: false } : f)),
    setAll: (ids: string[], value: boolean) =>
      setFolded((f) => ({ ...f, ...Object.fromEntries(ids.map((i) => [i, value])) })),
  };
}

export function Field({
  label,
  children,
  style,
}: {
  label: string;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <label className="field" style={style}>
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}

export function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone?: "up" | "down";
  /**
   * Plain-language gloss for a label that isn't self-evident (Sharpe, Ulcer,
   * Expectancy...). Rendered as a native tooltip with a dotted underline as the
   * hover affordance, so it isn't a silent no-op. Omit it for labels that
   * already say what they are — a hint on every stat is just noise.
   */
  hint?: string;
}) {
  return (
    <div className="stat">
      <div className={`stat__label${hint ? " has-hint" : ""}`} title={hint}>
        {label}
      </div>
      <div className={`stat__value${tone ? ` ${tone}` : ""}`}>{value}</div>
    </div>
  );
}

export function EmptyState({
  icon = "○",
  title,
  children,
  action,
}: {
  icon?: string;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-state__icon">{icon}</div>
      <div className="empty-state__title">{title}</div>
      {children && <div className="empty-state__body">{children}</div>}
      {action && <div style={{ marginTop: "1rem" }}>{action}</div>}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? "is-active" : undefined}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Badge({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: "success" | "danger" | "accent" | "warn";
}) {
  return <span className={`badge${tone ? ` badge--${tone}` : ""}`}>{children}</span>;
}

/**
 * A single current-color swatch that opens a small fixed palette on click — no
 * OS picker. Renders the popover via a portal into <body>: cards clip overflow
 * for their rounded corners, which would otherwise cut the popover's 2nd row.
 */
export function ColorSwatchPicker({
  value,
  options,
  onChange,
  title,
}: {
  value: string;
  options: readonly string[];
  onChange: (color: string) => void;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function toggle() {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
    setOpen((o) => !o);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="swatch-picker__trigger"
        style={{ background: value }}
        title={title ?? "Choose color"}
        onClick={toggle}
      />
      {open &&
        pos &&
        createPortal(
          <div ref={popoverRef} className="swatch-picker__popover" style={{ top: pos.top, left: pos.left }}>
            {options.map((color) => (
              <button
                key={color}
                type="button"
                className={`swatch-picker__option${color === value ? " is-selected" : ""}`}
                style={{ background: color }}
                title={color}
                onClick={() => {
                  onChange(color);
                  setOpen(false);
                }}
              />
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

export function Progress({ value }: { value: number }) {
  return (
    <div className="progress">
      <div className="progress__bar" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}
